import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { auth, db } from '@/firebase';
import { isAuthorizedReckonUser } from '@/lib/draftMode';

const COLLECTIONS = Object.freeze({
  narratives: 'narratives',
  venues: 'venues',
  events: 'events',
});

const recordsFromSnapshot = (snapshot) => snapshot.docs.map((document) => ({
  id: document.id,
  ...document.data(),
}));

const normalizeNarrative = (item) => ({
  ...item,
  category: item.category || 'Culture',
  href: item.href || (item.eventSlug
    ? {
      name: 'reckon-way-story',
      params: { gallerySlug: item.eventSlug, storySlug: item.slug },
    }
    : { name: 'reckon-way-culture-story', params: { storySlug: item.slug } }),
});

const bySequence = (left, right) => (
  (left.sequence ?? Number.POSITIVE_INFINITY) - (right.sequence ?? Number.POSITIVE_INFINITY)
  || (left.date || '').localeCompare(right.date || '')
  || (left.title || '').localeCompare(right.title || '')
);

const normalizeVenue = (item) => ({
  ...item,
  related: item.related || [],
  locations: item.locations || [item.location].filter(Boolean),
  destinations: item.destinations || [],
  visitDates: item.visitDates || [],
  journeys: item.journeys || [],
  href: item.href || (item.kind === 'eat'
    ? { name: 'reckon-way-eat', params: { eatSlug: item.slug } }
    : { name: 'reckon-way-place', params: { placeSlug: item.slug } }),
});

async function fetchDraftCollection(name) {
  const user = auth.currentUser;
  if (!isAuthorizedReckonUser(user)) throw new Error('Draft access requires an authorized Reckon account.');

  // Force a fresh token before each protected request. Firestore Rules make the final decision.
  await user.getIdToken(true);
  const draftQuery = query(collection(db, COLLECTIONS[name]), where('status', '==', 'draft'));
  return recordsFromSnapshot(await getDocs(draftQuery));
}

async function fetchPublishedCollection(name) {
  const publishedQuery = query(collection(db, COLLECTIONS[name]), where('status', '==', 'published'));
  return recordsFromSnapshot(await getDocs(publishedQuery));
}

export async function fetchPublishedContent() {
  const [narratives, venues, events] = await Promise.all([
    fetchPublishedCollection('narratives'),
    fetchPublishedCollection('venues'),
    fetchPublishedCollection('events'),
  ]);
  return {
    narratives: narratives.map(normalizeNarrative).sort(bySequence),
    venues: venues.map(normalizeVenue),
    events,
  };
}

export async function fetchProtectedDraftContent() {
  const [narratives, venues, events] = await Promise.all([
    fetchDraftCollection('narratives'),
    fetchDraftCollection('venues'),
    fetchDraftCollection('events'),
  ]);
  return {
    narratives: narratives.map(normalizeNarrative).sort(bySequence),
    venues: venues.map(normalizeVenue),
    events,
  };
}

export { COLLECTIONS };
